import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import DateInput from "../components/DateInput";
import { ArrowRight, MapPin, Calendar as CalendarIcon, Star } from "lucide-react";

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [location, setLocation] = useState("Downtown");
  const [pickup, setPickup] = useState("");
  const [ret, setRet] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/vehicles")
      .then((r) => {
        // Defend against non-array payloads (auth errors, unexpected wrappers).
        const list = Array.isArray(r?.data) ? r.data : Array.isArray(r?.data?.vehicles) ? r.data.vehicles : [];
        setVehicles(list.slice(0, 6));
      })
      .catch(() => setVehicles([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (pickup) params.set("pickup", pickup);
    if (ret) params.set("return", ret);
    if (location) params.set("location", location);
    navigate(`/vehicles?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-20 pt-12 md:pt-20 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          <div className="lg:col-span-7 fade-up">
            <p className="overline mb-6">Issue No. 24 — Spring Collection</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              Modern <em className="italic font-normal">Luxury</em>,
              <br />
              Delivered to <em className="italic font-normal">Your</em> Door.
            </h1>
            <p className="mt-8 text-base md:text-lg text-neutral-700 max-w-xl leading-relaxed">
              A curated rental experience for travellers, designers, and discerning drivers.
              Pick your moment — we'll handle the rest.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/vehicles"
                className="inline-flex items-center gap-2 bg-black text-white px-7 py-4 hover:bg-neutral-800 transition-colors text-sm tracking-wide"
                data-testid="hero-browse-button"
              >
                Browse the Fleet <ArrowRight size={16} />
              </Link>
              <Link
                to="/vehicles?category=luxury"
                className="inline-flex items-center gap-2 border border-black/20 hover:border-black px-7 py-4 transition-colors text-sm tracking-wide"
                data-testid="hero-luxury-button"
              >
                Explore Luxury
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up">
            <div className="relative overflow-hidden" style={{ aspectRatio: "4/5" }}>
              <img
                src="https://images.unsplash.com/photo-1681167816895-940c56e0d2a5?w=1200&q=85"
                alt="Car Rental App editorial"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/80 backdrop-blur-md p-4 flex justify-between items-center">
                <div>
                  <p className="overline">Featured</p>
                  <p className="font-display text-lg mt-1">Rolls-Royce Phantom</p>
                </div>
                <p className="text-sm">€980<span className="text-neutral-500">/day</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <form
          onSubmit={handleSearch}
          className="mt-16 bg-white border border-black/10 p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-end"
          data-testid="search-form"
        >
          <div className="md:col-span-4">
            <label className="overline block mb-2">Pickup Location</label>
            <div className="flex items-center gap-2 border-b border-black/20 pb-2 focus-within:border-black">
              <MapPin size={16} className="text-neutral-500" />
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="flex-1 bg-transparent outline-none text-base"
                data-testid="search-location-input"
                placeholder="City or airport"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="overline block mb-2">Pick-up</label>
            <div className="flex items-center gap-2 border-b border-black/20 pb-2 focus-within:border-black">
              <CalendarIcon size={16} className="text-neutral-500" />
              <DateInput
                value={pickup}
                onChange={setPickup}
                className="border-b-0 py-0 pr-8"
                testId="search-pickup-date"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <label className="overline block mb-2">Return</label>
            <div className="flex items-center gap-2 border-b border-black/20 pb-2 focus-within:border-black">
              <CalendarIcon size={16} className="text-neutral-500" />
              <DateInput
                value={ret}
                onChange={setRet}
                className="border-b-0 py-0 pr-8"
                testId="search-return-date"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              className="w-full bg-black text-white py-3 hover:bg-neutral-800 transition-colors text-sm tracking-wide"
              data-testid="search-submit-button"
            >
              Search
            </button>
          </div>
        </form>
      </section>

      {/* MARQUEE */}
      <section className="overflow-hidden py-6 border-y border-black/5">
        <div className="marquee-track flex gap-16 whitespace-nowrap font-display text-2xl md:text-4xl">
          {[..."FERRARI · PORSCHE · TESLA · BMW · ROLLS-ROYCE · MERCEDES-BENZ · LAND ROVER · JEEP · ".repeat(4)].join("")}
        </div>
      </section>

      {/* FEATURED FLEET — Bento */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="flex justify-between items-end mb-12">
          <div>
            <p className="overline mb-3">Selected Fleet</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">
              The <em className="italic">collection</em>.
            </h2>
          </div>
          <Link
            to="/vehicles"
            className="hidden md:inline-flex items-center gap-2 text-sm link-underline"
            data-testid="view-all-link"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {(Array.isArray(vehicles) ? vehicles : []).slice(0, 6).map((v, i) => {
            // bento varied sizing
            const spans = [
              "md:col-span-7", "md:col-span-5",
              "md:col-span-4", "md:col-span-4", "md:col-span-4",
              "md:col-span-12",
            ];
            return (
              <Link
                key={v.id}
                to={`/vehicles/${v.id}`}
                className={`editorial-card group block ${spans[i] || "md:col-span-4"}`}
                data-testid={`featured-vehicle-${v.id}`}
              >
                <div className="relative overflow-hidden bg-[#EFECE6]" style={{ aspectRatio: i === 5 ? "21/9" : "4/3" }}>
                  <img
                    src={v.image_url}
                    alt={v.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-white px-3 py-1 text-xs uppercase tracking-wider">
                    {v.category}
                  </div>
                </div>
                <div className="p-6 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider">{v.brand}</p>
                    <p className="font-display text-2xl mt-1">{v.name}</p>
                    <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
                      <Star size={12} fill="currentColor" /> {v.rating} · {v.seats} seats · {v.transmission}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-2xl" data-testid={`card-price-${v.id}`}>
                      €{Number((v.has_duration_tiers ? v.display_rate : v.daily_rate) || 0).toFixed(0)}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {v.has_duration_tiers ? "Tier 1 · per day" : "per day (default)"}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* MANIFESTO */}
      <section
        className="px-6 md:px-12 lg:px-20 py-24"
        style={{ background: "#0A0A0A", color: "#F6F5F2" }}
      >
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="overline mb-6" style={{ color: "#A8A8A8" }}>Our Manifesto</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              We don't rent cars. <br />
              We rent <em className="italic">moments</em> behind the wheel.
            </h2>
            <p className="mt-6 text-neutral-300 max-w-xl leading-relaxed">
              Every vehicle in our collection is hand-picked, professionally maintained, and
              delivered fully detailed. Whether it's a weekend escape or a board meeting in
              the city — we believe how you arrive matters.
            </p>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-6">
            {[
              { k: "24/7", v: "Concierge" },
              { k: "150+", v: "Vehicles" },
              { k: "40k", v: "Happy drivers" },
              { k: "4.9★", v: "Average rating" },
            ].map((s) => (
              <div key={s.v} className="border-l border-white/20 pl-4 py-2">
                <p className="font-display text-3xl">{s.k}</p>
                <p className="text-xs uppercase tracking-wider text-neutral-400 mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-6 md:px-12 lg:px-20 py-24">
        <p className="overline mb-3">How it works</p>
        <h2 className="font-display text-4xl md:text-5xl mb-16 tracking-tight">
          Three steps to <em className="italic">the road</em>.
        </h2>
        <div className="grid md:grid-cols-3 gap-12">
          {[
            { n: "01", t: "Choose", d: "Browse a curated fleet — from city-friendly EVs to weekend grand tourers." },
            { n: "02", t: "Book", d: "Select your dates, add insurance and extras, sign the rental agreement online." },
            { n: "03", t: "Drive", d: "We deliver the keys to your door. You drive. We handle the rest." },
          ].map((s) => (
            <div key={s.n}>
              <p className="font-display text-6xl md:text-7xl text-neutral-300">{s.n}</p>
              <div className="divider-line mt-4 mb-4"></div>
              <p className="font-display text-2xl mb-3">{s.t}</p>
              <p className="text-sm text-neutral-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
